import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatCurrency } from "@/lib/format";
import { SERVICE_LANES } from "@/config/resources";
import { REVENUE_TYPES } from "@/config/revenueOptions";
import { CASE_STATUSES } from "@/config/caseOptions";
import { REPORT_TYPES, LEAD_SOURCES, CURRENCY_KEYS } from "@/config/reportOptions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from "recharts";
import {
  Users2, CalendarHeart, FolderHeart, CheckCircle2, TrendingUp, Download, Printer,
  BarChart3, FileSpreadsheet, AlertTriangle, Handshake, Dumbbell, Trophy, Filter,
} from "lucide-react";
import { toast } from "sonner";

const NAVY = "#1C4358", RED = "#9C1E22", SKY = "#9BB9CE", GREEN = "#2F7D5A";
const COLORS = [NAVY, RED, SKY, "#C4C89B", GREEN, "#B7791F", "#252221"];

const StatCard = ({ icon: Icon, label, value, color, testid }) => (
  <Card className="p-4 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid={testid}>
    <div className="flex items-center gap-3">
      <span className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}1A` }}><Icon size={19} style={{ color }} /></span>
      <div className="min-w-0"><div className="text-2xl font-bold font-display tabular-nums text-[#252221] leading-none">{value ?? 0}</div><div className="text-[11px] text-muted-foreground mt-1 truncate">{label}</div></div>
    </div>
  </Card>
);

const ChartCard = ({ title, data, color = NAVY, money = false, testid }) => {
  const arr = Object.entries(data || {}).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 8);
  return (
    <Card className="p-5 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid={testid}>
      <h3 className="font-display font-semibold text-[#252221] mb-3">{title}</h3>
      {arr.length === 0 ? <p className="text-sm text-muted-foreground py-10 text-center">No data yet.</p> : (
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={arr} layout="vertical" margin={{ left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EEF3F6" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11, fill: "#4B5563" }} tickFormatter={money ? (v) => `$${(v / 1000).toFixed(0)}k` : undefined} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: "#4B5563" }} width={130} />
            <Tooltip formatter={(v) => (money ? formatCurrency(v) : v)} cursor={{ fill: "rgba(155,185,206,0.12)" }} />
            <Bar dataKey="value" radius={[0, 6, 6, 0]}>{arr.map((_, i) => <Cell key={i} fill={money ? color : COLORS[i % COLORS.length]} />)}</Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
};

const emptyFilters = { start: "", end: "", branch_id: "all", country: "all", state: "", service_lane: "all", lead_source: "all", revenue_type: "all", case_status: "all", assigned_user: "all" };

export default function Reports() {
  const { activeBranch } = useAuth();
  const [tab, setTab] = useState("analytics");
  const [analytics, setAnalytics] = useState(null);
  const [loadingA, setLoadingA] = useState(true);

  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState(emptyFilters);
  const [reportType, setReportType] = useState("lead_source");
  const [report, setReport] = useState(null);
  const [loadingR, setLoadingR] = useState(false);

  const buildParams = useCallback(() => {
    const p = {};
    if (activeBranch) p.branch_id = activeBranch;
    if (filters.branch_id !== "all") p.branch_id = filters.branch_id;
    if (filters.country !== "all") p.country = filters.country;
    if (filters.state) p.state = filters.state;
    if (filters.start) p.start = filters.start;
    if (filters.end) p.end = filters.end;
    if (filters.service_lane !== "all") p.service_lane = filters.service_lane;
    if (filters.lead_source !== "all") p.lead_source = filters.lead_source;
    if (filters.revenue_type !== "all") p.revenue_type = filters.revenue_type;
    if (filters.case_status !== "all") p.case_status = filters.case_status;
    if (filters.assigned_user !== "all") p.assigned_user = filters.assigned_user;
    return p;
  }, [activeBranch, filters]);

  const loadAnalytics = useCallback(async () => {
    setLoadingA(true);
    try {
      const { data } = await api.get("/analytics", { params: buildParams() });
      setAnalytics(data);
    } finally { setLoadingA(false); }
  }, [buildParams]);

  const loadReport = useCallback(async () => {
    setLoadingR(true);
    try {
      const { data } = await api.get("/reports/run", { params: { type: reportType, ...buildParams() } });
      setReport(data);
    } catch { toast.error("Could not load report"); } finally { setLoadingR(false); }
  }, [reportType, buildParams]);

  useEffect(() => { if (tab === "analytics") loadAnalytics(); }, [tab, loadAnalytics]);
  useEffect(() => { if (tab === "reports") loadReport(); }, [tab, loadReport]);
  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data.items || [])).catch(() => {});
    api.get("/users").then((r) => setUsers(r.data.items || r.data || [])).catch(() => setUsers([]));
  }, []);

  const fmtCell = (key, val) => (CURRENCY_KEYS.has(key) ? formatCurrency(Number(val) || 0) : (val === "" || val == null ? "—" : String(val)));

  const exportCSV = () => {
    if (!report) return;
    const head = report.columns.map((c) => `"${c.label}"`).join(",");
    const body = report.rows.map((r) => report.columns.map((c) => {
      const v = r[c.key];
      return `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
    }).join(",")).join("\n");
    const csv = `${head}\n${body}`;
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `${report.type}_report.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
    toast.success("CSV exported");
  };

  const escHtml = (v) => String(v == null ? "" : v).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  const printReport = () => {
    if (!report) return;
    const win = window.open("", "_blank", "width=900,height=700");
    if (!win) { toast.error("Allow pop-ups to print"); return; }
    const summaryHtml = Object.entries(report.summary || {}).map(([k, v]) => `<span style="margin-right:18px"><strong>${escHtml(k)}:</strong> ${escHtml(v)}</span>`).join("");
    const head = report.columns.map((c) => `<th style="text-align:left;padding:8px 10px;border-bottom:2px solid #1C4358;font-size:12px;color:#1C4358">${escHtml(c.label)}</th>`).join("");
    const rowsHtml = report.rows.map((r) => `<tr>${report.columns.map((c) => `<td style="padding:7px 10px;border-bottom:1px solid #E6EDF2;font-size:13px">${escHtml(fmtCell(c.key, r[c.key]))}</td>`).join("")}</tr>`).join("");
    win.document.write(`<html><head><title>${escHtml(report.title)}</title></head><body style="font-family:Arial,sans-serif;padding:28px;color:#252221">
      <h1 style="font-size:20px;color:#1C4358;margin:0 0 4px">MovingMyMama — ${escHtml(report.title)}</h1>
      <div style="font-size:12px;color:#6b7280;margin-bottom:6px">Generated ${new Date().toLocaleString()}</div>
      <div style="font-size:13px;margin-bottom:14px">${summaryHtml}</div>
      <table style="width:100%;border-collapse:collapse"><thead><tr>${head}</tr></thead><tbody>${rowsHtml}</tbody></table>
    </body></html>`);
    win.document.close();
    setTimeout(() => { win.focus(); win.print(); }, 350);
  };

  const filterBar = (showReportFilters) => (
    <Card className="p-3 border-[#E6EDF2] mb-4" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
      <div className="flex items-center gap-1.5 mb-2 text-xs font-semibold text-[#1C4358]/70"><Filter size={13} /> FILTERS</div>
      <div className="flex flex-wrap items-end gap-2">
        <div><Label className="text-[11px] text-muted-foreground mb-1 block">From</Label><Input type="date" value={filters.start} onChange={(e) => setFilters((p) => ({ ...p, start: e.target.value }))} className="h-9 w-[150px]" data-testid="filter-start" /></div>
        <div><Label className="text-[11px] text-muted-foreground mb-1 block">To</Label><Input type="date" value={filters.end} onChange={(e) => setFilters((p) => ({ ...p, end: e.target.value }))} className="h-9 w-[150px]" data-testid="filter-end" /></div>
        <div><Label className="text-[11px] text-muted-foreground mb-1 block">Branch</Label>
          <Select value={filters.branch_id} onValueChange={(v) => setFilters((p) => ({ ...p, branch_id: v }))}>
            <SelectTrigger className="h-9 w-[160px]" data-testid="filter-branch"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All branches</SelectItem>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.branch_name || b.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-[11px] text-muted-foreground mb-1 block">Country</Label>
          <Select value={filters.country} onValueChange={(v) => setFilters((p) => ({ ...p, country: v }))}>
            <SelectTrigger className="h-9 w-[120px]" data-testid="filter-country"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="USA">USA</SelectItem><SelectItem value="Canada">Canada</SelectItem></SelectContent>
          </Select>
        </div>
        <div><Label className="text-[11px] text-muted-foreground mb-1 block">Service lane</Label>
          <Select value={filters.service_lane} onValueChange={(v) => setFilters((p) => ({ ...p, service_lane: v }))}>
            <SelectTrigger className="h-9 w-[160px]" data-testid="filter-lane"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All lanes</SelectItem>{SERVICE_LANES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {showReportFilters && (
          <>
            <div><Label className="text-[11px] text-muted-foreground mb-1 block">Lead source</Label>
              <Select value={filters.lead_source} onValueChange={(v) => setFilters((p) => ({ ...p, lead_source: v }))}>
                <SelectTrigger className="h-9 w-[140px]" data-testid="filter-source"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All sources</SelectItem>{LEAD_SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-[11px] text-muted-foreground mb-1 block">Revenue type</Label>
              <Select value={filters.revenue_type} onValueChange={(v) => setFilters((p) => ({ ...p, revenue_type: v }))}>
                <SelectTrigger className="h-9 w-[170px]" data-testid="filter-revtype"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-[280px]"><SelectItem value="all">All types</SelectItem>{REVENUE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-[11px] text-muted-foreground mb-1 block">Case status</Label>
              <Select value={filters.case_status} onValueChange={(v) => setFilters((p) => ({ ...p, case_status: v }))}>
                <SelectTrigger className="h-9 w-[170px]" data-testid="filter-casestatus"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-[280px]"><SelectItem value="all">All statuses</SelectItem>{CASE_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-[11px] text-muted-foreground mb-1 block">Assigned user</Label>
              <Select value={filters.assigned_user} onValueChange={(v) => setFilters((p) => ({ ...p, assigned_user: v }))}>
                <SelectTrigger className="h-9 w-[160px]" data-testid="filter-user"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-[260px]"><SelectItem value="all">Anyone</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name || u.email}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </>
        )}
        <Button variant="ghost" size="sm" onClick={() => setFilters(emptyFilters)} className="h-9" data-testid="clear-filters">Clear</Button>
      </div>
    </Card>
  );

  return (
    <div>
      <div className="mb-4">
        <h2 className="font-display text-2xl font-bold text-[#252221] flex items-center gap-2"><BarChart3 size={22} className="text-[#1C4358]" /> Reports & Internal Analytics</h2>
        <p className="text-sm text-muted-foreground mt-0.5">Business-focused insight across leads, cases, revenue, partners, and team performance.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="analytics" data-testid="tab-analytics"><TrendingUp size={15} className="mr-1.5" /> Analytics dashboard</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports"><FileSpreadsheet size={15} className="mr-1.5" /> Reports</TabsTrigger>
        </TabsList>

        {/* ANALYTICS */}
        <TabsContent value="analytics" className="mt-4">
          {filterBar(false)}
          {loadingA || !analytics ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4" data-testid="analytics-cards">
                <StatCard icon={CalendarHeart} label="Consultations booked" value={analytics.consultations_booked} color={SKY} testid="a-consult-booked" />
                <StatCard icon={CheckCircle2} label="Consultations completed" value={analytics.consultations_completed} color={GREEN} testid="a-consult-completed" />
                <StatCard icon={FolderHeart} label="Cases opened" value={analytics.cases_opened} color={NAVY} testid="a-cases-opened" />
                <StatCard icon={CheckCircle2} label="Cases closed" value={analytics.cases_closed} color={GREEN} testid="a-cases-closed" />
                <StatCard icon={Handshake} label="Partner referrals" value={analytics.partner_referrals} color={NAVY} testid="a-referrals" />
                <StatCard icon={Dumbbell} label="Trilogy event leads" value={analytics.trilogy_event_leads} color={GREEN} testid="a-trilogy" />
                <StatCard icon={AlertTriangle} label="Overdue tasks" value={analytics.overdue_tasks} color={RED} testid="a-overdue" />
                <StatCard icon={Users2} label="Total leads" value={analytics.totals?.leads} color={NAVY} testid="a-leads" />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                <ChartCard title="Leads by source" data={analytics.leads_by_source} testid="chart-leads-source" />
                <ChartCard title="Leads by branch" data={analytics.leads_by_branch} testid="chart-leads-branch" />
                <ChartCard title="Revenue by service lane" data={analytics.revenue_by_lane} color={GREEN} money testid="chart-rev-lane" />
                <ChartCard title="Revenue by branch" data={analytics.revenue_by_branch} color={RED} money testid="chart-rev-branch" />
              </div>
              <Card className="border-[#E6EDF2] overflow-hidden" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="team-performance">
                <div className="p-5 pb-3 flex items-center gap-2"><Trophy size={17} className="text-[#B7791F]" /><h3 className="font-display font-semibold text-[#252221]">Team performance</h3></div>
                {(analytics.team_performance || []).length === 0 ? <p className="text-sm text-muted-foreground px-5 pb-6">No assigned activity yet.</p> : (
                  <div className="overflow-x-auto mmm-scroll">
                    <Table>
                      <TableHeader><TableRow className="hover:bg-transparent">
                        <TableHead className="text-xs font-semibold text-[#1C4358]/80">Team member</TableHead>
                        <TableHead className="text-xs font-semibold text-[#1C4358]/80">Cases</TableHead>
                        <TableHead className="text-xs font-semibold text-[#1C4358]/80">Tasks completed</TableHead>
                        <TableHead className="text-xs font-semibold text-[#1C4358]/80">Revenue</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {analytics.team_performance.map((t) => (
                          <TableRow key={t.user_id} className="hover:bg-[rgba(155,185,206,0.10)]">
                            <TableCell className="py-3 font-medium text-[#252221]">{t.name}</TableCell>
                            <TableCell className="tabular-nums">{t.cases}</TableCell>
                            <TableCell className="tabular-nums">{t.tasks_completed}</TableCell>
                            <TableCell className="tabular-nums font-semibold text-[#2F7D5A]">{formatCurrency(t.revenue)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </Card>
            </>
          )}
        </TabsContent>

        {/* REPORTS */}
        <TabsContent value="reports" className="mt-4">
          {filterBar(true)}
          <Card className="p-3 border-[#E6EDF2] mb-4 flex flex-wrap items-center gap-3" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium text-[#252221]">Report</Label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger className="h-9 w-[240px]" data-testid="report-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-[360px]">{REPORT_TYPES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={exportCSV} disabled={!report} data-testid="export-csv-button"><Download size={15} className="mr-1.5" /> CSV</Button>
              <Button variant="outline" size="sm" onClick={printReport} disabled={!report} data-testid="print-button"><Printer size={15} className="mr-1.5" /> Print / PDF</Button>
            </div>
          </Card>

          <Card className="border-[#E6EDF2] overflow-hidden" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="report-output">
            <div className="p-5 pb-3 flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-display font-semibold text-[#252221]">{report?.title || "Report"}</h3>
              {report?.summary && Object.keys(report.summary).length > 0 && (
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground" data-testid="report-summary">
                  {Object.entries(report.summary).map(([k, v]) => <span key={k}><span className="font-semibold text-[#1C4358]">{k}:</span> {typeof v === "number" && /revenue|realized|paid/i.test(k) ? formatCurrency(v) : v}</span>)}
                </div>
              )}
            </div>
            {loadingR ? (
              <div className="p-5 space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}</div>
            ) : !report || report.rows.length === 0 ? (
              <p className="text-sm text-muted-foreground px-5 pb-6">No data for this report and the selected filters.</p>
            ) : (
              <div className="overflow-x-auto mmm-scroll">
                <Table data-testid="report-table">
                  <TableHeader><TableRow className="hover:bg-transparent">
                    {report.columns.map((c) => <TableHead key={c.key} className="text-xs font-semibold text-[#1C4358]/80 whitespace-nowrap">{c.label}</TableHead>)}
                  </TableRow></TableHeader>
                  <TableBody>
                    {report.rows.map((r, idx) => (
                      <TableRow key={idx} className="hover:bg-[rgba(155,185,206,0.10)]" data-testid="report-row">
                        {report.columns.map((c) => <TableCell key={c.key} className="text-sm tabular-nums whitespace-nowrap">{fmtCell(c.key, r[c.key])}</TableCell>)}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            {report && report.rows.length > 0 && <p className="text-xs text-muted-foreground px-5 py-3">{report.rows.length} row{report.rows.length === 1 ? "" : "s"}</p>}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
